import { createTrackerForCategory } from '@gonfalon/analytics/createTrackerForCategory';

import { markSettingsEntry, markSettingsNavigation, type SettingsEntrySource } from './settingsSession';

const trackSettings = createTrackerForCategory('Settings');

const MAX_QUERY_LENGTH = 100;

export function normalizeSettingsQuery(query: string) {
  return query.trim().toLowerCase().slice(0, MAX_QUERY_LENGTH);
}

export type SettingsBackToProjectSource = 'gear' | 'back-link';

export const trackSettingsOpened = (attributes: {
  entry: SettingsEntrySource;
  target: 'settings' | 'project-settings' | 'organization-settings';
  fromPath: string;
  flattened: boolean;
}) => {
  markSettingsEntry(attributes.entry);
  trackSettings('Settings Opened', attributes);
};

export const trackSettingsBackToProjectClicked = (attributes: {
  source: SettingsBackToProjectSource;
  fromPath: string;
}) => trackSettings('Settings Back To Project Clicked', attributes);

export const trackSettingsNavItemClicked = (attributes: {
  key: string;
  label: string;
  section: string;
  searchedFirst: boolean;
}) => {
  markSettingsNavigation('rail');
  trackSettings('Settings Nav Item Clicked', attributes);
};

export const trackSettingsSearched = (attributes: {
  query: string;
  queryLength: number;
  pageResultCount: number;
  headingResultCount: number;
  hasResults: boolean;
}) => trackSettings('Settings Search Performed', attributes);

export const trackSettingsSearchPageResultClicked = (attributes: {
  key: string;
  label: string;
  query: string;
  matchedOn: 'page' | 'heading';
  position: number;
  resultCount: number;
}) => {
  markSettingsNavigation('search-page');
  trackSettings('Settings Search Page Result Clicked', attributes);
};

export const trackSettingsSearchHeadingResultClicked = (attributes: {
  key: string;
  label: string;
  heading: string;
  query: string;
  position: number;
  resultCount: number;
}) => {
  markSettingsNavigation('search-heading');
  trackSettings('Settings Search Heading Result Clicked', attributes);
};

export const trackSettingsHeadingScrolled = (attributes: {
  heading: string;
  found: boolean;
  headingLevel?: string;
  matchType?: 'heading' | 'text';
  elapsedMs: number;
  surface: 'page' | 'drawer';
}) => trackSettings('Settings Heading Scrolled To', { ...attributes, metricValue: attributes.elapsedMs });

export const trackSettingsBreadcrumbClicked = (attributes: { crumb: 'settings'; fromPath: string }) => {
  markSettingsNavigation('breadcrumb');
  trackSettings('Settings Breadcrumb Clicked', attributes);
};

export const trackSettingsBreadcrumbScopeChanged = (attributes: {
  scope: 'project' | 'environment';
  page: 'sdk-keys';
  fromKey?: string;
  toKey?: string;
}) => {
  markSettingsNavigation('breadcrumb');
  trackSettings('Settings Breadcrumb Scope Changed', attributes);
};

export const trackProjectSettingsDrawerOpened = (attributes: { source: 'search-heading' | 'direct' }) =>
  trackSettings('Project Settings Drawer Opened', attributes);

export const trackProjectSettingsDrawerErrorShown = () => trackSettings('Project Settings Drawer Error Shown');

export type ProjectSettingsDrawerCloseMethod = 'close-button' | 'dismiss' | 'deleted' | 'navigation';

export const trackProjectSettingsDrawerClosed = (attributes: {
  method: ProjectSettingsDrawerCloseMethod;
  openMs: number;
}) => trackSettings('Project Settings Drawer Closed', { ...attributes, metricValue: attributes.openMs });

const trackProjectSwitcher = createTrackerForCategory('Project Switcher');

export type ProjectSwitcherSource = 'sidebar' | 'settings-breadcrumb';

export const trackProjectSwitcherOpened = (attributes: { source: ProjectSwitcherSource; fromPath: string }) =>
  trackProjectSwitcher('Project Switcher Opened', attributes);

export const trackProjectSwitcherProjectSelected = (attributes: {
  source: ProjectSwitcherSource;
  itemType: 'recent' | 'project';
  searched: boolean;
  queryLength: number;
  position: number;
  fromPath: string;
}) => {
  if (attributes.source === 'settings-breadcrumb') {
    markSettingsNavigation('breadcrumb');
  }
  trackProjectSwitcher('Project Switcher Project Selected', attributes);
};

export const trackProjectSwitcherShowAllClicked = (attributes: { source: ProjectSwitcherSource; fromPath: string }) =>
  trackProjectSwitcher('Project Switcher Show All Clicked', attributes);

export type SettingsFormSurface = 'vega-project-configuration' | 'observability-project-settings';

export const trackSettingsFormSaveSubmitted = (attributes: {
  surface: SettingsFormSurface;
  fieldsChanged: number;
  fieldsAvailable: number;
  fields: string[];
}) =>
  trackSettings('Settings Form Save Submitted', {
    ...attributes,
    fields: [...attributes.fields].sort().join(','),
    metricValue: attributes.fieldsChanged,
  });
